import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {EChartsOption} from 'echarts';

export interface ResponsiveTreeLayout {
    left: number;
    right: number;
    top: number;
    bottom: number;
    symbolSize: [number, number];
}

export const TREE_CARD_HEIGHT = 48;
export const TREE_CARD_MAX_WIDTH = 242;

export function updateResponsiveTreeLabelWidth(series: any, cardWidth: number): void {
    [series?.label?.formatter, series?.emphasis?.label?.formatter]
        .forEach(formatter => formatter?.setTreeCardWidth?.(cardWidth));
}

export function visibleTreeColumnCounts(tree: any, initialTreeDepth = 1): number[] {
    const counts: number[] = [];
    const visit = (node: any, depth: number) => {
        counts[depth] = (counts[depth] || 0) + 1;
        const expanded = depth < initialTreeDepth || node.collapsed === false;
        if (expanded) (node.children || []).forEach(child => visit(child, depth + 1));
    };
    if (tree) visit(tree, 0);
    return counts;
}

export function responsiveTreeLayout(containerWidth: number, containerHeight = 0, tree?: any): ResponsiveTreeLayout {
    const width = Math.max(0, containerWidth);
    const outerPadding = Math.min(32, Math.max(16, Math.round(width * 0.03)));
    const columns = visibleTreeColumnCounts(tree);
    const columnCount = Math.max(columns.length, 2);
    const availableNodeWidth = Math.max(80, width - outerPadding * 2);
    const depthAwareWidth = Math.floor(availableNodeWidth / (columnCount + Math.max(0, columnCount - 1) * 0.32));
    // The depth-aware limit already guarantees that every visible lane fits.
    // An additional viewport-percentage limit made two-lane cards needlessly
    // narrow on medium screens, even when both full-width cards fit comfortably.
    const symbolWidth = Math.min(TREE_CARD_MAX_WIDTH, availableNodeWidth, depthAwareWidth);
    const horizontalInset = Math.ceil(symbolWidth / 2 + outerPadding);
    const height = Math.max(0, containerHeight);
    const desiredVerticalPadding = Math.min(52, Math.max(42, Math.round(height * 0.12)));
    const busiestColumn = Math.max(0, ...columns);
    const deckInsetReduction = Math.min(14, Math.max(0, busiestColumn - 7) * 2);
    const deckAwareVerticalPadding = Math.max(TREE_CARD_HEIGHT / 2 + 4,
        desiredVerticalPadding - deckInsetReduction);
    const maximumVerticalPadding = Math.max(16, Math.floor((height - TREE_CARD_HEIGHT) / 2));
    const verticalPadding = height > 0 ? Math.min(deckAwareVerticalPadding, maximumVerticalPadding) : 42;
    // Busy columns deliberately overlap like a card deck. Shrinking every node to
    // its allotted row produced pill-shaped nodes and made labels harder to scan.
    const symbolHeight = TREE_CARD_HEIGHT;
    return {
        left: horizontalInset,
        right: horizontalInset,
        top: verticalPadding,
        bottom: verticalPadding,
        symbolSize: [symbolWidth, symbolHeight]
    };
}

@Component({
    selector: 'app-tree-chart',
    templateUrl: './tree-chart.component.html',
    styleUrls: ['./tree-chart.component.css']
})
export class TreeChartComponent implements OnInit, OnChanges, OnDestroy {
    @Input() options!: EChartsOption;
    @Input() chartKey!: string;
    @Output() chartInit = new EventEmitter<{ instance: any, key: string }>();
    @Output() chartClick = new EventEmitter<{ event: any, key: string }>();

    @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

    private chartInstance: any;
    private resizeObserver?: ResizeObserver;

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.options && this.chartInstance) {
            Promise.resolve().then(() => this.resizeChart());
        }
    }

    onChartInit(ec: any): void {
        this.chartInstance = ec;
        this.chartInit.emit({ instance: ec, key: this.chartKey });
        this.resizeObserver = new ResizeObserver(() => this.resizeChart());
        this.resizeObserver.observe(this.chartContainer.nativeElement);
        this.resizeChart();
    }

    onChartClick(event: any): void {
        this.chartClick.emit({ event, key: this.chartKey });
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private resizeChart(): void {
        if (!this.chartInstance || !this.chartContainer?.nativeElement) return;
        const series = Array.isArray(this.options?.series) ? this.options.series[0] : this.options?.series;
        const tree = (series as any)?.data?.[0];
        const layout = responsiveTreeLayout(
            this.chartContainer.nativeElement.clientWidth,
            this.chartContainer.nativeElement.clientHeight,
            tree
        );
        updateResponsiveTreeLabelWidth(series, layout.symbolSize[0]);
        this.chartInstance.setOption({series: [{type: 'tree', ...layout}]});
        this.chartInstance.resize();
    }
}
